import Button from "components/shared/button";
import Loader from "components/shared/loader";
import dayjs from "dayjs";
import { Link, useNavigate } from "react-router";
import { useGetEtudesListQuery, useDuplicateEtudeMutation } from "store/api";
import { APP_URL, EtudeStep, getAppUrl } from "utils/defines";
import { useSalesNetwork } from "utils/hooks/useSalesNetworks";
import useTranslation from "utils/hooks/useTranslation";
import ContextMenuList from "./ContextMenu/ContextMenuList";
import { usePopover } from "providers/PopoverProvider";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { usePopup } from "providers/PopupProvider";
import { MailPopup } from "./popups/MailPopup";
import { DeletePopup } from "./popups/DeletePopup";
import { EtudeTypes } from "./EtudeForm/constants";
import { SavePopup } from "./popups/SavePopup";
import { PdfDownloadPopup } from "./popups/PdfDownloadPopup";

export const ShortEtudeList: React.FC<{ etudesCount: number }> = ({ etudesCount }) => {
  const { t, language } = useTranslation();
  const { openPopup, closeLastPopup } = usePopup();
  const navigate = useNavigate();
  const { network, isLoading: isSalesNetworkLoading } = useSalesNetwork();

  const currentSelectedRowID = useRef<number[]>([]);
  const currentSelectedRowService = useRef<string>("");
  const currentSelectedRowSolutionID = useRef<string>("");
  const [currentSelectedRowStep, setCurrentSelectedRowStep] =
    useState<number>(0);
  const [savedEvent, setSavedEvent] = useState<React.MouseEvent | null>(null);
  const currentSelectedTPID = useRef<number | null>(null);
  const [savedRowName, setSavedRowName] = useState<string>("");

  const {
    data: etudesData,
    isLoading: isEtudeListDataLoading,
    refetch: refetchEtudeListData,
  } = useGetEtudesListQuery({
    code: network?.code,
  });
  let originalEtudesListData = etudesData?.etudes;
  const [etudesListData, setEtudesListData] = useState<any>(
    originalEtudesListData
  );
  useEffect(() => {
    if (etudesListData) {
      setEtudesListData(etudesListData);
    }
  }, [etudesListData]);

  const getEtudePath = useCallback(
    (versionStep = currentSelectedRowStep) => {
      let path;
      const etudeType = currentSelectedRowService.current;
      const etudeId = currentSelectedRowID.current;
      const solutionDetailID = currentSelectedRowSolutionID.current;
      const solutionDetailIDEncoded = encodeURIComponent(solutionDetailID);
      const step = versionStep;

      switch (step) {
        case EtudeStep.InputForm:
          path = `/etude/${etudeType}/${etudeId}`;
          break;
        case EtudeStep.Solutions:
          path = `/etude/${etudeType}/${etudeId}/solutions`;
          break;
        case EtudeStep.SolutionDetails:
          path = `/etude/${etudeType}/${etudeId}/solutions/${solutionDetailIDEncoded}`;
          break;
        case EtudeStep.Accessoires:
          path = `/etude/${etudeType}/${etudeId}/solutions/${solutionDetailIDEncoded}/accessoires`;
          break;
        case EtudeStep.Documents:
          path = `/etude/${etudeType}/${etudeId}/documents`;
          break;
      }

      return path;
    },
    [currentSelectedRowStep]
  );

  const onMoreOpenClick = useCallback(() => {
    const path = getEtudePath();

    navigate(getAppUrl(path as any, { network: network?.code }));
  }, [getEtudePath, navigate, network]);

  const handleRowOpen = useCallback(
    (data: any) => {
      try {
        const versions = JSON.parse(data.versions || "[]").reverse();
        const selVersion =
          versions.find((v: any) => v.ID == data.id) || versions[0];
        const version =
          selVersion && selVersion.json ? JSON.parse(selVersion.json) : {};

        // update refs / state so getEtudePath() can read correct values
        currentSelectedRowID.current = [data.id];
        currentSelectedRowService.current = data.service;
        currentSelectedRowSolutionID.current = version.selectedSolutionID ?? "";
        setCurrentSelectedRowStep(version.step ?? EtudeStep.InputForm);

        // now build path from refs (getEtudePath reads refs)
        const path = getEtudePath(version.step);
        navigate(getAppUrl(path as any, { network: network?.code }));
      } catch (err) {
        console.error("handleRowOpen error:", err);
        // fallback: navigate to basic etude route
        navigate(
          getAppUrl(`/etude/${data.service}/${encodeURIComponent(data.id)}`, {
            network: network?.code,
          })
        );
      }
    },
    [getEtudePath, navigate, network]
  );

  const onMoreSendClick = useCallback(() => {
    // const path = getAppUrl(getEtudePath(), { network: network?.code });

    openPopup(
      <MailPopup
        etudeIds={JSON.stringify(currentSelectedRowID.current)}
        showPrice={false}
        isEtudeDetail={false}
        culture={language!}
        networkId={network?.id}
      // etudeURL={path}
      />
    );
  }, [openPopup, language, network]);

  const [duplicateEtudeMutation] = useDuplicateEtudeMutation();

  const onMoreDuplicateClick = useCallback(() => {
    duplicateEtudeMutation({ etudeVersionId: currentSelectedRowID.current[0] }).then((result: any) => {
      const newEtudeId = result?.data;
      if (newEtudeId.id || newEtudeId.ID) {
        navigate(
          getAppUrl(`/etude/${currentSelectedRowService.current}/${newEtudeId.id || newEtudeId.ID}`, {
            network: network?.code,
          })
        );
      } else {
        refetchEtudeListData();
      }
    });
  }, [duplicateEtudeMutation, navigate, network, refetchEtudeListData]);

  const [isPdfURLLoading, setIsPdfURLLoading] = useState<boolean>(false);

  const onMoreDownloadClick = useCallback(() => {
    openPopup(
      <PdfDownloadPopup
        network={network}
        selectedRowIDs={currentSelectedRowID.current}
        isPdfURLLoading={isPdfURLLoading}
        setIsPdfURLLoading={setIsPdfURLLoading}
        isEtudeDetail={false}
      />
    );
  }, []);

  const onMoreEditClick = useCallback(() => {
    openPopup(
      <SavePopup
        closePopup={closeLastPopup}
        isRenaming
        renamingEtudeId={currentSelectedTPID.current}
        currentName={savedRowName}
        refetchEtudeListData={refetchEtudeListData}
      />
    );
  }, [openPopup, closeLastPopup, refetchEtudeListData, savedRowName]);

  const onMoreDeleteClick = useCallback(() => {
    openPopup(
      <DeletePopup
        closePopup={closeLastPopup}
        network={network}
        selectedRowIDs={currentSelectedRowID.current}
        etudesListData={etudesListData}
        setEtudesListDataCallback={setEtudesListData}
        refetchEtudeListData={refetchEtudeListData}
      />
    );
  }, [
    openPopup,
    closeLastPopup,
    network,
    etudesListData,
    setEtudesListData,
    refetchEtudeListData,
  ]);

  const moreMenuItems = useMemo(
    () => [
      {
        text: t("FO_SIM_OPEN"),
        icon: "open_in_new",
        onClick() {
          onMoreOpenClick();
        },
      },
      {
        text: t("upload"),
        icon: "save",
        hide: currentSelectedRowStep < EtudeStep.Documents,
        onClick() {
          onMoreDownloadClick();
        },
      },
      {
        text: t("FO_STUDIES_RENAME"),
        icon: "edit",
        onClick() {
          onMoreEditClick();
        },
      },
      {
        text: t("FO_STUDIES_SEND"),
        icon: "mail",
        hide: currentSelectedRowStep < EtudeStep.Documents,
        onClick() {
          onMoreSendClick();
        },
      },
      {
        text: t("FO_STUDIES_DUPLICATE"),
        icon: "content_copy",
        onClick() {
          onMoreDuplicateClick();
        },
      },
      { separator: true },
      {
        text: t("FO_STUDIES_DELETE"),
        icon: "delete",
        onClick() {
          onMoreDeleteClick();
        },
      },
    ],
    [
      t,
      currentSelectedRowStep,
      onMoreOpenClick,
      onMoreDownloadClick,
      onMoreEditClick,
      onMoreSendClick,
      onMoreDeleteClick,
      onMoreDuplicateClick,
    ]
  );

  const { open } = usePopover();
  const onMoreClick = useCallback(
    (e: React.MouseEvent) => {
      open({
        element: (e.target as any).closest("[data-popover-trigger]"),
        content: <ContextMenuList data-space="5" items={moreMenuItems} />,
      });
    },
    [open, moreMenuItems]
  );

  useEffect(() => {
    if (savedEvent) {
      onMoreClick(savedEvent);
      setSavedEvent(null);
    }
  }, [savedEvent, currentSelectedRowStep, moreMenuItems, onMoreClick]);

  const etudeListElems: any = [];

  if (originalEtudesListData) {
    for (let i = 0; i < etudesCount; i++) {
      if (originalEtudesListData[i]) {
        etudeListElems.push({
          createdAt:
            originalEtudesListData[i].createdAt ||
            originalEtudesListData[i].updatedAt,
          description: originalEtudesListData[i].siteName,
          service: originalEtudesListData[i].service,
          data: originalEtudesListData[i],
        });
      }
    }
  }

  return (
    <>
      {(isEtudeListDataLoading || isPdfURLLoading || isSalesNetworkLoading) && (
        <Loader />
      )}
      {originalEtudesListData?.length > 0 ? (
        <group data-direction="column" data-gap="15" data-name="custom-list">
          {etudeListElems.map((etude: any) => (
            <group
              key={etude.data.key}
              data-height="80"
              data-border=""
              data-radius="5"
              data-contain=""
              data-wrap="no"
              data-gap="10"
              data-cursor="pointer"
              data-name="custom-list-item"
              data-align="center"
            >
              <group
                data-length="70"
                data-ratio="1:1"
                data-align="center"
                data-justify="center"
                data-color="main"
                data-name="custom-list-thumb"
              >
                {EtudeTypes.chauffage == etude.service ? (
                  <icon data-icon-size="48">heat</icon>
                ) : EtudeTypes.ECS == etude.service ? (
                  <icon data-icon-size="48">water_drop</icon>
                ) : (
                  <>
                    <icon data-icon-size="24">water_drop</icon>
                    <icon data-icon-size="24">heat</icon>
                  </>
                )}
                <separator
                  data-vertical=""
                  data-height="fit"
                  data-margin="0"
                ></separator>
              </group>
              <group
                data-direction="column"
                data-justify="center"
                data-space="10"
                data-contain=""
                data-name="custom-list-content"
                onClick={() => handleRowOpen(etude.data)}
              >
                <group data-length="autofit">
                  <text>{dayjs(etude.createdAt).format("DD/MM/YYYY")}</text>
                </group>
                <text
                  data-weight="700"
                  data-size="medium"
                  data-ellipsis=""
                  data-name="custom-list-content-description"
                >
                  {etude.description}
                </text>
                <text>
                  {etude.service?.charAt(0).toUpperCase() +
                    etude.service?.slice(1)}
                </text>
              </group>
              <group
                data-align="center"
                data-justify="end"
                data-gap="10"
                data-space="10"
                data-position="right"
                data-name="custom-list-panel"
                data-wrap="no"
                data-length="autofit"
              >
                <group data-popover-trigger data-custom="">
                  <Button
                    micro
                    rounded
                    icon="more_vert"
                    onClick={(e: any) => {
                      const versions = JSON.parse(etude.data.versions);
                      const version = JSON.parse(versions[0]?.json);

                      currentSelectedRowID.current = [etude.data.id!];
                      currentSelectedRowService.current = etude.data.service;
                      currentSelectedRowSolutionID.current =
                        version.selectedSolutionID;
                      currentSelectedTPID.current =
                        etude.data.thercodim_projectId;

                      setSavedRowName(etude.data.siteName);
                      setCurrentSelectedRowStep(version.step);
                      setSavedEvent(e);
                    }}
                  />
                </group>
              </group>
            </group>
          ))}
          <group>
            <div className="button fit accent large">
              <Link to={getAppUrl(APP_URL.MyEtudes)}>
                <group data-align="center" data-justify="center" data-gap="5">
                  <icon>summarize</icon>
                  <text data-width="auto">
                    {t("FO_WELCOME_SEE_MY_STUDIES")}
                  </text>
                </group>
              </Link>
            </div>
          </group>
        </group>
      ) : (
        <text data-text-size="15">{t("FOWEL_NO_STUDIES")}</text>
      )}
    </>
  );
};
